import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useParams } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';

import GoBackToListButton from '../../components/buttons/GoBackToListButton';
import {
  CoursesBySemesters,
  Legends,
  Resume,
} from '../../components/curriculum';
import { LegendsProps } from '../../components/curriculum/Legends';
import { ResumeProps } from '../../components/curriculum/Resume';
import { SectionTitle } from '../../components/text';
import { PathsLayouts } from '../../config/routes';
import { isNumber } from '../../utils/math';
import BaseLoader from './parts/BaseLoader';
import CurriculumLoader from './parts/CurriculumLoader';
import StudyPlanBox from './parts/StudyPlanBox';
import {
  CourseStatusTooltip,
  OptionalCourseStatusTooltip,
} from '../CurriculumProgressStudent/types/progress';
import ModalCreateValidation, {
  ModalCreateValidationMethods,
} from './parts/ModalCreateValidation';

export default function CourseValidationInternalStudent() {
  const prefix = 'courseValidationInternalStudent';
  const { t } = useTranslation();

  const createValidationRef = useRef<ModalCreateValidationMethods>(null);

  const texts = useMemo(
    () => ({
      body: t(`${prefix}.body`),
      resumeTitle: t(`${prefix}.resume.title`),
      curriculumTitle: t(`${prefix}.curriculum.title`),
      semester: t(`${prefix}.curriculum.semester`),
      credits: t(`${prefix}.curriculum.credits`),
      optionalToolTip: ({
        credits,
        totalCredits,
      }: OptionalCourseStatusTooltip) =>
        t(`${prefix}.curriculum.optionalStatusTooltip`, {
          credits,
          totalCredits,
        }),
      convalidationToolTip: ({
        validationType,
        validationSubType,
        examGrade,
        finalGrade,
        externalCourseName,
        externalGrade,
        code,
      }: CourseStatusTooltip) =>
        t(
          `${prefix}.curriculum.statusTooltip.${
            validationSubType || validationType
          }`,
          {
            examGrade,
            finalGrade,
            externalCourseName,
            externalGrade,
            code,
          },
        ),
      degreeActivityType: t(`${prefix}.curriculum.degreeActivityType`),
      degreeActivityTooltip: t(`${prefix}.curriculum.degreeActivityTooltip`),
      box: {
        student: t(`${prefix}.box.student`),
        rut: t(`${prefix}.box.rut`),
        studyPlan: t(`${prefix}.box.studyPlan`),
      },
      resume: {
        ...t<string, ResumeProps['texts']>(`${prefix}.resume`, {
          returnObjects: true,
        }),
        durationValue: (duration: number, periodsType: string) =>
          t(`${prefix}.resume.durationValue`, { count: duration, periodsType }),
      },
      curriculumStatus: t<string, LegendsProps['texts']>(
        `${prefix}.curriculum.status`,
        {
          returnObjects: true,
        },
      ),
    }),
    [t],
  );

  const { studyPlanEnrollmentId: studyPlanEnrollmentIdStr } = useParams<{
    studyPlanEnrollmentId: string;
  }>();

  const studyPlanEnrollmentId = useMemo(() => {
    if (isNumber(studyPlanEnrollmentIdStr)) {
      return Number(studyPlanEnrollmentIdStr);
    }
    return null;
  }, [studyPlanEnrollmentIdStr]);

  if (!studyPlanEnrollmentId) {
    return <Redirect to={PathsLayouts.courseValidationInternal} />;
  }

  return (
    <Card className="p-4 mt-2 mx-3">
      <BaseLoader studyPlanEnrollmentId={studyPlanEnrollmentId}>
        {(data) => (
          <>
            <Row>
              <Col xs={12} sm={3} className="pb-2">
                <GoBackToListButton
                  path={PathsLayouts.courseValidationInternal}
                />
              </Col>
              <Col xs={12} sm={9} className="pb-2">
                <StudyPlanBox
                  studentName={`${data.student.lastnames}, ${data.student.name}`}
                  studentRut={data.student.rut}
                  studyPlanName={data.studyPlanVersion?.name}
                  texts={texts.box}
                />
              </Col>
            </Row>
            <p className="text-medium fs-16 mt-3 mb-5">{texts.body}</p>
            <CurriculumLoader studyPlanEnrollmentId={studyPlanEnrollmentId}>
              {({ data: curriculumData, refresh }) => (
                <>
                  {/* DATOS DE LA MALLA */}
                  <div className="mb-4">
                    <div className="mb-3">
                      <SectionTitle text={texts.resumeTitle} />
                    </div>
                    <Resume texts={texts.resume} {...curriculumData} />
                  </div>

                  {/* MALLA DE ESTUDIOS */}
                  <div>
                    <div className="mb-3">
                      <SectionTitle text={texts.curriculumTitle} />
                    </div>
                    <div className="mb-3">
                      <CoursesBySemesters
                        extraCourses={curriculumData.extraCourses}
                        semesters={curriculumData.semesters}
                        texts={texts}
                        onClick={(semesterCourse) => {
                          createValidationRef?.current?.openSemesterCourse({
                            semesterCourse,
                            studyPlanEnrollmentId,
                          });
                        }}
                      />

                      <ModalCreateValidation
                        ref={createValidationRef}
                        onSave={refresh}
                      />
                    </div>
                    <Legends texts={texts.curriculumStatus} />
                  </div>
                </>
              )}
            </CurriculumLoader>
          </>
        )}
      </BaseLoader>
    </Card>
  );
}
